// web/src/components/FeedbackButton.tsx — "הערה לשיפור" בכל מסך של הצוות (28.09 · v2 · מקביל ל-GL-10 + DEC 423 ברשגד).
// 📄 מוטי 28.09: "צילום מסך ולקשקש עליו, הקלטה קולית ותמלול אוטומטי… שיהיה לעתידה נוח ומזמין לכתוב הערות… להעלות תמונה או קובץ."
// 🔑 כתיבה · הקלטה (התמלול נכנס לשדה — היא רואה ומתקנת; ההקלטה עצמה נשלחת גם היא) · קובץ או תמונה · צילום מסך + סימון · דחוף.
// 🔑 נשמר עם ההערה, כטקסט: המסך, רוחב, מגע, חיבור, ו-5 השגיאות האחרונות בדף. ⛔ מייל ודרופבוקס (אלה של רמי).
// 🔑 הקבצים עולים ל-Supabase Storage (דלי פרטי) באסימון שלה; השרת רושם אותם רק אחרי שהגיעו (092).
// 🔑 טיוטת הטקסט נשמרת בדפדפן — סגירה בטעות לא מוחקת. מונה על הכפתור = תשובה חדשה ממוטי.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { dk, toApiError } from "../api/client";
import type { components } from "../api/gen/dk";
import { useAuth } from "../auth/AuthProvider";
import { shrinkImage, uploadFeedbackFile } from "../lib/storage";
import { DrawEditor } from "./DrawEditor";

type Feedback = components["schemas"]["Feedback"];
type Kind = "image" | "screenshot" | "file" | "audio";
type Att = { key: string; kind: Kind; blob: Blob; name: string; url: string; state: "ready" | "uploading" | "done" | "failed" };

const KINDS: { k: "broken" | "missing" | "awkward"; label: string }[] = [
  { k: "broken", label: "לא עובד" }, { k: "missing", label: "חסר" }, { k: "awkward", label: "לא נוח" },
];
const STATUS: Record<string, string> = { sent: "נשלחה", seen: "מוטי ראה", done: "טופלה ✓" };
const DRAFT = "atida.feedback.draft";
const MAX_REC_SEC = 180;
const SILENT = 0.01;   // עוצמה ממוצעת (RMS) שמתחתיה ההקלטה נחשבת שקטה — דיבור רגיל ≈ 0.05 ומעלה

// סיומת הקובץ באחסון לפי הסוג האמיתי (תמונה שהוקטנה היא JPEG גם אם נקראה ‎.png)
const EXT: Record<string, string> = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/gif": "gif",
  "audio/webm": "webm", "audio/mp4": "m4a", "audio/ogg": "ogg", "application/pdf": "pdf" };
const storeName = (b: Blob, fallback: string) => { const e = EXT[b.type.split(";")[0] ?? ""]; return e ? `file.${e}` : fallback; };

// ── שגיאות אחרונות בדף (הקשר אוטומטי, כמו ברשגד) ──
const recentErrors: string[] = [];
function noteError(s: string) { recentErrors.push(`${new Date().toLocaleTimeString("he-IL")} ${s}`.slice(0, 300)); if (recentErrors.length > 5) recentErrors.shift(); }
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => noteError(e.message || "שגיאה"));
  window.addEventListener("unhandledrejection", (e) => noteError(String((e as PromiseRejectionEvent).reason ?? "שגיאה")));
}

function readDraft() { try { return window.localStorage.getItem(DRAFT) ?? ""; } catch { return ""; } }
function writeDraft(v: string) { try { if (v) window.localStorage.setItem(DRAFT, v); else window.localStorage.removeItem(DRAFT); } catch { /* חסום */ } }
const kb = (n: number) => (n > 1_000_000 ? `${(n / 1_000_000).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1000))}KB`);
const hhmm = () => new Date().toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

function context(path: string) {
  const touch = matchMedia("(pointer: coarse)").matches ? "מגע" : "עכבר";
  const lines = [`מסך: ${path}`, `רוחב ${innerWidth}×${innerHeight} · ${touch} · ${navigator.onLine ? "מחובר" : "בלי חיבור"}`];
  if (recentErrors.length) lines.push("שגיאות אחרונות:", ...recentErrors);
  return lines.join("\n");
}

function pickMime() {
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

async function toBase64(b: Blob): Promise<string> {
  const buf = new Uint8Array(await b.arrayBuffer());
  let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

// ── צילום מסך (29.09 · הערת מוטי: "מטושטש עד שאי אפשר לקרוא… מציג טקסטים שעולים אחד על השני") ──
// 🔑 הסיבות הסבירות (לא נמדדו ישירות): (1) השחזור מה-DOM צייר את כל הדף — בשולחן הוא ארוך מאוד, והספרייה מקטינה קנבס מעל 16384px ⇒ טשטוש.
//    (2) הגופנים של גוגל לא הוטמעו בשחזור ⇒ גופן חלופי במידות אחרות ⇒ טקסטים שעולים זה על זה.
// 🔑 הפתרון: במחשב — צילום אמיתי של הלשונית דרך הדפדפן (פיקסל-לפיקסל; הדפדפן מבקש אישור בלחיצה אחת).
//    בטלפון (אין תמיכה) — שחזור של החלון הנראה בלבד, ברזולוציה כפולה, עם הגופנים מוטמעים.
const HIDE = ["fb-btn", "fb-backdrop", "desk-toast", "tf-ui"];   // tf-ui — הכדור והבועות של תפארת (29.09)
let fontCss: Promise<string> | null = null;

async function captureTab(): Promise<Blob | null> {
  const md = navigator.mediaDevices as MediaDevices | undefined;
  if (!md?.getDisplayMedia || matchMedia("(pointer: coarse)").matches) return null;
  let stream: MediaStream;
  try {
    stream = await md.getDisplayMedia({
      video: { displaySurface: "browser" }, audio: false,
      preferCurrentTab: true, selfBrowserSurface: "include", surfaceSwitching: "exclude",
    } as DisplayMediaStreamOptions);
  } catch (e) {
    if (e instanceof DOMException && e.name === "NotAllowedError") throw new Error("cancelled");
    return null;                                              // לא נתמך בפועל ⇒ שחזור
  }
  try {
    const v = document.createElement("video");
    v.muted = true; v.playsInline = true; v.srcObject = stream;
    await v.play();
    await new Promise((r) => setTimeout(r, 400));             // חלון האישור של הדפדפן נסגר והדף מצטייר מחדש
    if (!v.videoWidth) return null;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d")!.drawImage(v, 0, 0);
    return await new Promise<Blob | null>((ok) => c.toBlob(ok, "image/jpeg", 0.92));
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

async function renderViewport(): Promise<Blob | null> {
  const { toBlob, getFontEmbedCSS } = await import("html-to-image");
  const root = (document.querySelector(".shell") as HTMLElement | null) ?? document.body;
  const r = root.getBoundingClientRect();
  if (!fontCss) fontCss = getFontEmbedCSS(root).catch(() => { fontCss = null; return ""; });
  const w = Math.round(Math.min(innerWidth, r.width));
  const h = Math.round(Math.min(innerHeight, r.bottom));
  return await toBlob(root, {
    width: w, height: h,                                      // רק החלון הנראה ⇒ אין הקטנה של קנבס ענק
    pixelRatio: 2,
    fontEmbedCSS: await fontCss,
    backgroundColor: getComputedStyle(document.body).backgroundColor || "#fff",
    style: { transform: `translate(${Math.round(r.left)}px, ${Math.round(r.top)}px)`, transformOrigin: "top left" },
    filter: (n) => !(n instanceof HTMLElement && HIDE.some((k) => n.classList.contains(k))),
    type: "image/jpeg", quality: 0.92,
  });
}

export type FeedbackProps = {
  /** 🆕 29.09 — תפארת: החלון נפתח מתוכה (מצב "הערה למוטי"). בלי הפרופס — עובד לבד כמו קודם. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** הכפתור הצף מוסתר — תפארת היא הכפתור. */
  hideButton?: boolean;
  /** טקסט והקשר שנכנסים לשדה (בדיקה בסיור שלא עברה). key חדש = מילוי חדש. */
  prefill?: { key: number; text: string; context: string } | null;
  onSent?: (feedbackId: string) => void;
  /** "← תפארת" בראש החלון. */
  onBack?: () => void;
  /** מונה התשובות החדשות ממוטי — מוצג על הכדור של תפארת. */
  onUnseen?: (n: number) => void;
  /** 🆕 29.09 לילה — בתוך החלון של תפארת (מצב "הערה למוטי", כמו ברשגד): בלי חלון וכותרת משלו; התוכן נכנס ל-slot.
   *  הרכיב עצמו נשאר טעון גם כשהחלון סגור — טיוטה, צרופות וצילום בעריכה לא הולכים לאיבוד. */
  embedded?: boolean;
  slot?: HTMLElement | null;
};

export function FeedbackButton(props: FeedbackProps = {}) {
  const loc = useLocation();
  const { state: auth } = useAuth();
  const tenantId = auth.status === "signedIn" ? auth.me.tenant_id ?? "" : "";
  const [openLocal, setOpenLocal] = useState(false);
  const open = props.open ?? openLocal;
  const setOpen = (v: boolean) => { if (props.onOpenChange) props.onOpenChange(v); else setOpenLocal(v); };
  const [extra, setExtra] = useState("");                             // הקשר נוסף מתפארת (סיור · שלב)
  const [text, setText] = useState(readDraft);
  const [kind, setKind] = useState<"broken" | "missing" | "awkward" | null>(null);
  const [urgent, setUrgent] = useState(false);
  const [atts, setAtts] = useState<Att[]>([]);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "err">("idle");
  const [errText, setErrText] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);   // נשלחה, אבל צרופה נכשלה ⇒ "נסי שוב" משלים
  const [mine, setMine] = useState<Feedback[]>([]);
  const [unseen, setUnseen] = useState(0);
  const [showMine, setShowMine] = useState(false);
  const [drawing, setDrawing] = useState<{ blob: Blob; replace?: string } | null>(null);
  const [rec, setRec] = useState<{ sec: number; level: number; silent: boolean } | null>(null);
  const [stt, setStt] = useState<"" | "working" | string>("");
  const recRef = useRef<{ mr: MediaRecorder; chunks: Blob[]; timer: number; stream: MediaStream } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => dk.GET("/staff/feedback").then(({ data }) => { if (data) { setMine(data.items); setUnseen(data.unseen_replies); } }).catch(() => undefined);
  useEffect(() => { void load(); }, []);
  useEffect(() => { writeDraft(text); }, [text]);
  useEffect(() => { props.onUnseen?.(unseen); }, [unseen]);   // eslint-disable-line react-hooks/exhaustive-deps
  // 🆕 29.09 — בדיקה בסיור שלא עברה: המשפט נכנס לשדה (אחרי מה שכבר כתוב), הסוג "לא עובד", וההקשר נשמר לשליחה.
  useEffect(() => {
    const p = props.prefill;
    if (!p) return;
    setText((t) => (t.trim() ? `${t.trim()}\n${p.text}` : p.text));
    setKind("broken"); setExtra(p.context); setState("idle");
  }, [props.prefill?.key]);                                    // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && !drawing && !rec) setOpen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, drawing, rec]);
  useEffect(() => () => atts.forEach((a) => URL.revokeObjectURL(a.url)), []);   // eslint-disable-line react-hooks/exhaustive-deps

  function addAtt(kind: Kind, blob: Blob, name: string) {
    setAtts((xs) => [...xs, { key: crypto.randomUUID(), kind, blob, name, url: URL.createObjectURL(blob), state: "ready" }]);
    setState("idle");
  }
  function removeAtt(key: string) {
    setAtts((xs) => { const a = xs.find((x) => x.key === key); if (a) URL.revokeObjectURL(a.url); return xs.filter((x) => x.key !== key); });
  }

  // ── קובץ / תמונה ──
  async function onFiles(list: FileList | null) {
    if (!list) return;
    for (const f of Array.from(list)) {
      if (f.size > 15 * 1024 * 1024) { setErrText(`"${f.name}" גדול מ-15MB.`); continue; }
      if (f.type.startsWith("image/")) {
        const s = await shrinkImage(f);
        addAtt("image", s, s.type !== f.type ? f.name.replace(/\.[^.]+$/, "") + ".jpg" : f.name);   // הוקטנה ⇒ JPEG, גם בשם
      }
      else addAtt("file", f, f.name);
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  // ── צילום מסך ──
  async function screenshot() {
    setOpen(false);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const real = await captureTab();                       // מחשב: צילום אמיתי של הלשונית
      const blob = real ?? (await renderViewport());         // טלפון / דפדפן שלא תומך: שחזור מה-DOM
      if (!blob) throw new Error("no image");
      setDrawing({ blob });
    } catch (e) {
      setErrText(e instanceof Error && e.message === "cancelled"
        ? "הצילום בוטל. אפשר לנסות שוב, או לצרף תמונה מהמכשיר."
        : "הצילום לא הצליח. אפשר לצרף תמונה מהמכשיר במקום.");
      setOpen(true);
    }
  }

  // ── הקלטה + תמלול ──
  // 📄 29.09: הקלטה של 15 שניות יצאה 4KB — המיקרופון שנבחר לא קלט. ⇒ מד עוצמה חי, אזהרה כשלא נשמע קול,
  //    והקלטה שקטה לגמרי לא נשלחת לתמלול (מצורפת בכל זאת, כדי שאפשר יהיה לשמוע).
  async function startRec() {
    setErrText(""); setStt("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks: Blob[] = [];
      const meter = { peak: 0 };
      let ctx: AudioContext | null = null, an: AnalyserNode | null = null;
      try {
        ctx = new AudioContext();
        an = ctx.createAnalyser();
        an.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(an);
      } catch { ctx = null; an = null; meter.peak = 1; }          // אין מד ⇒ לא מזהירים
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = () => { void ctx?.close().catch(() => undefined); void finishRec(new Blob(chunks, { type: mr.mimeType || mime || "audio/webm" }), meter.peak); };
      mr.start(1000);
      const started = Date.now();
      const buf = an ? new Uint8Array(an.fftSize) : null;
      const timer = window.setInterval(() => {
        const sec = Math.floor((Date.now() - started) / 1000);
        let level = 0;
        if (an && buf) {
          an.getByteTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) { const d = (v - 128) / 128; sum += d * d; }
          level = Math.sqrt(sum / buf.length);
          meter.peak = Math.max(meter.peak, level);
        }
        setRec({ sec, level, silent: sec >= 3 && meter.peak < SILENT });
        if (sec >= MAX_REC_SEC) stopRec();
      }, 200);
      recRef.current = { mr, chunks, timer, stream };
      setRec({ sec: 0, level: 0, silent: false });
    } catch {
      setErrText("אין גישה למיקרופון. אפשר לאשר אותו בהגדרות הדפדפן, או לכתוב.");
    }
  }
  function stopRec() {
    const r = recRef.current;
    if (!r) return;
    window.clearInterval(r.timer);
    if (r.mr.state !== "inactive") r.mr.stop();
    r.stream.getTracks().forEach((t) => t.stop());
    recRef.current = null;
    setRec(null);
  }
  async function finishRec(blob: Blob, peak = 1) {
    if (blob.size < 1000) return;
    addAtt("audio", blob, `הקלטה ${hhmm()}`);
    if (peak < SILENT) {
      setStt("ההקלטה יצאה שקטה כמעט לגמרי — כנראה נבחר מיקרופון אחר. אפשר להחליף מיקרופון בהגדרות הדפדפן ולהקליט שוב.");
      return;
    }
    setStt("working");
    const { data, error, response } = await dk.POST("/staff/feedback/transcribe", {
      body: { audio_base64: await toBase64(blob), mime_type: blob.type || "audio/webm" },
    }).catch(() => ({ data: undefined, error: undefined, response: { status: 0 } as Response }));
    if (data?.text) {
      setText((t) => (t.trim() ? `${t.trim()}\n${data.text}` : data.text));
      setStt("");
    } else {
      setStt(response.status ? toApiError(response.status, error).message : "התמלול לא הצליח. ההקלטה תישלח כמו שהיא.");
    }
  }

  // ── שליחה ──
  async function uploadAll(fid: string, list: Att[]) {
    let failed = 0;
    for (const a of list) {
      if (a.state === "done") continue;
      setAtts((xs) => xs.map((x) => (x.key === a.key ? { ...x, state: "uploading" } : x)));
      try {
        const path = await uploadFeedbackFile(tenantId, a.blob, storeName(a.blob, a.name));
        const { data } = await dk.POST("/staff/feedback/{feedback_id}/attachments", {
          params: { path: { feedback_id: fid } },
          body: { kind: a.kind, storage_path: path, file_name: a.name, mime_type: a.blob.type || "application/octet-stream" },
        });
        if (!data) throw new Error("attach");
        setAtts((xs) => xs.map((x) => (x.key === a.key ? { ...x, state: "done" } : x)));
      } catch {
        failed += 1;
        setAtts((xs) => xs.map((x) => (x.key === a.key ? { ...x, state: "failed" } : x)));
      }
    }
    return failed;
  }

  async function send() {
    if (!text.trim() && !atts.length) return;
    setState("sending"); setErrText("");
    let fid = pendingId;
    if (!fid) {
      const { data, error, response } = await dk.POST("/staff/feedback", {
        body: { body: text, kind, urgent, screen: loc.pathname, context: context(loc.pathname) + (extra ? `\n${extra}` : ""), with_attachments: atts.length > 0 },
      }).catch(() => ({ data: undefined, error: undefined, response: { status: 0 } as Response }));
      if (!data) { setState("err"); setErrText(response.status ? toApiError(response.status, error).message : "אין חיבור. ההערה נשמרה כאן — אפשר לנסות שוב."); return; }
      fid = data.id;
    }
    const failed = atts.length ? await uploadAll(fid, atts) : 0;
    if (failed) {
      setPendingId(fid); setState("err");
      setErrText(failed === 1 ? "ההערה נשלחה, אבל צרופה אחת לא עלתה. \"שליחה\" תנסה שוב רק אותה." : `ההערה נשלחה, אבל ${failed} צרופות לא עלו. "שליחה" תנסה שוב רק אותן.`);
      return;
    }
    setState("sent"); setText(""); setKind(null); setUrgent(false); setPendingId(null); setStt(""); setExtra("");
    atts.forEach((a) => URL.revokeObjectURL(a.url)); setAtts([]);
    props.onSent?.(fid);
    void load();
  }

  async function openMine() {
    setShowMine((v) => !v);
    if (unseen) { await dk.POST("/staff/feedback/replies-seen").catch(() => undefined); setUnseen(0); }
  }

  const dirty = !!text || atts.length > 0;
  const canSend = (!!text.trim() || atts.length > 0) && state !== "sending" && !rec && stt !== "working";
  const content = (
    <>
            <p className="muted small">מה לא נוח, מה חסר, מה לא ברור — אפשר לכתוב, להקליט, לצלם את המסך ולסמן עליו, או לצרף קובץ. זה מגיע ישר למוטי.</p>

            <div className="field" style={{ marginBottom: 10 }}>
              <label htmlFor="fb-text" className="visually-hidden">ההערה</label>
              <textarea id="fb-text" rows={4} value={text} placeholder="למשל: הכפתור הזה קטן מדי בטלפון"
                        onChange={(e) => { setText(e.target.value); setState("idle"); }}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && canSend) void send(); }} />
            </div>

            <div className="fb-actions">
              {rec ? (
                <button className="fb-action fb-rec-on" onClick={stopRec}>
                  <span className="fb-rec-dot" aria-hidden="true" />
                  <span>{Math.floor(rec.sec / 60)}:{String(rec.sec % 60).padStart(2, "0")} · לסיום</span>
                  <span className="fb-level" aria-hidden="true"><span style={{ width: `${Math.min(100, Math.round(rec.level * 500))}%` }} /></span>
                </button>
              ) : (
                <button className="fb-action" onClick={() => void startRec()} disabled={stt === "working"}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v5" /></svg>
                  <span>הקלטה</span>
                </button>
              )}
              <button className="fb-action" onClick={() => fileRef.current?.click()} disabled={!!rec}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M21 12.5l-8.5 8.5a5 5 0 0 1-7-7L14 5.5a3.5 3.5 0 0 1 5 5L10.5 19a2 2 0 0 1-3-3L15 8.5" /></svg>
                <span>קובץ או תמונה</span>
              </button>
              <button className="fb-action" onClick={() => void screenshot()} disabled={!!rec}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
                <span>צילום מסך</span>
              </button>
              <input ref={fileRef} type="file" multiple hidden onChange={(e) => void onFiles(e.target.files)} />
            </div>

            {rec?.silent && <p className="warn-text small" role="alert">לא נשמע קול. ייתכן שנבחר מיקרופון אחר — אפשר לעצור, להחליף בהגדרות הדפדפן ולהקליט שוב.</p>}
            {stt === "working" && <p className="muted small" role="status">מתמללת את ההקלטה…</p>}
            {stt && stt !== "working" && <p className="warn-text small" role="alert">{stt}</p>}

            {atts.length > 0 && (
              <ul className="fb-atts">
                {atts.map((a) => (
                  <li key={a.key} className={`fb-att is-${a.state}`}>
                    {a.kind === "audio" ? (
                      <audio controls src={a.url} className="fb-audio" />
                    ) : a.kind === "file" ? (
                      <span className="fb-file-ico" aria-hidden="true">📄</span>
                    ) : (
                      <img src={a.url} alt="" className="fb-thumb" />
                    )}
                    <div className="fb-att-meta">
                      <div className="small">{a.kind === "screenshot" ? "צילום מסך" : a.name}</div>
                      <div className="muted small">{kb(a.blob.size)}{a.state === "uploading" ? " · עולה…" : a.state === "done" ? " · עלה ✓" : a.state === "failed" ? " · לא עלה" : ""}</div>
                    </div>
                    {(a.kind === "image" || a.kind === "screenshot") && a.state !== "done" && (
                      <button className="btn btn-quiet btn-sm" onClick={() => setDrawing({ blob: a.blob, replace: a.key })}>סימון</button>
                    )}
                    {a.state !== "done" && a.state !== "uploading" && (
                      <button className="btn btn-quiet btn-sm" aria-label="הסרה" onClick={() => removeAtt(a.key)}>✕</button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="row-wrap" role="group" aria-label="סוג (לא חובה)" style={{ marginTop: 10 }}>
              {KINDS.map((x) => (
                <button key={x.k} type="button" className={`tag${kind === x.k ? " tag-on" : ""}`} aria-pressed={kind === x.k}
                        onClick={() => setKind(kind === x.k ? null : x.k)}>{x.label}</button>
              ))}
            </div>
            <p className="muted small" style={{ marginTop: 8 }}>מצורף אוטומטית: המסך {loc.pathname} ופרטי המכשיר.</p>
            <div className="row-between">
              <label className="check"><input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> דחוף</label>
              <button className="btn btn-primary" disabled={!canSend} onClick={() => void send()}>
                {state === "sending" ? "שולחת…" : "שליחה"}
              </button>
            </div>
            {state === "sent" && <p className="ok-text" role="status">נשלח ✓ תודה! מוטי יראה את זה.</p>}
            {errText && <p className="warn-text small" role="alert">{errText}</p>}

            {mine.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <button className="linklike" onClick={() => void openMine()}>
                  ההערות ששלחת · {mine.length}{unseen ? ` · ${unseen} תשובות חדשות` : ""}
                </button>
                {showMine && (
                  <ul className="fb-mine">
                    {mine.slice(0, 20).map((f) => (
                      <li key={f.id}>
                        <div className="small">{f.body || "(צרופות בלבד)"}{f.attachments?.length ? ` · ${f.attachments.length} צרופות` : ""}</div>
                        <div className="muted small">{new Date(f.created_at).toLocaleDateString("he-IL")} · {STATUS[f.status]}{f.urgent ? " · דחוף" : ""}</div>
                        {f.reply && <div className="small fb-reply">↩ {f.reply}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
    </>
  );

  return (
    <>
      {!props.hideButton && (
      <button className="fb-btn" aria-label="הערה לשיפור" title="הערה לשיפור" onClick={() => { setOpen(!open); if (state === "sent") setState("idle"); }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18h6" /><path d="M10 22h4" />
          <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V17h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" />
        </svg>
        {unseen > 0 ? <span className="fb-count" aria-label={`${unseen} תשובות חדשות`}>{unseen}</span> : dirty ? <span className="fb-dot" aria-hidden="true" /> : null}
      </button>
      )}

      {open && !props.embedded && (
        <div className="fb-backdrop" onClick={() => { if (!rec) setOpen(false); }}>
          <div className="fb-panel card" role="dialog" aria-label="הערה לשיפור" onClick={(e) => e.stopPropagation()}>
            <div className="row-between">
              {props.onBack && (
                <button className="btn btn-quiet btn-sm tf-back" disabled={!!rec} onClick={() => props.onBack?.()}>→ תפארת</button>
              )}
              <h2 style={{ margin: 0 }}>{props.onBack ? "הערה למוטי" : "הערה לשיפור"}</h2>
              <button className="btn btn-quiet btn-sm" aria-label="סגירה" disabled={!!rec} onClick={() => setOpen(false)}>✕</button>
            </div>
            {content}
          </div>
        </div>
      )}
      {open && props.embedded && props.slot && createPortal(<div className="fb-embedded">{content}</div>, props.slot)}

      {drawing && (
        <DrawEditor
          image={drawing.blob}
          onCancel={() => { setDrawing(null); setOpen(true); }}   // צילום חדש שבוטל ⇒ לא מצורף (כמו ברשגד)
          onDone={(b) => {
            if (drawing.replace) {
              setAtts((xs) => xs.map((x) => {
                if (x.key !== drawing.replace) return x;
                URL.revokeObjectURL(x.url);
                return { ...x, blob: b, url: URL.createObjectURL(b), state: "ready" };
              }));
            } else {
              addAtt("screenshot", b, `צילום מסך ${hhmm()}`);
            }
            setDrawing(null);
            setOpen(true);
          }}
        />
      )}
    </>
  );
}
